import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  ReactElement,
  ReactNode,
} from "react";
import "./DropdownMenu.css";

export interface DropdownMenuItem {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  /** Renders the item in `--fc-danger`. Destructive actions sit last. */
  danger?: boolean;
  disabled?: boolean;
  /** Right-aligned keyboard hint, e.g. `⌘⌫`. */
  shortcut?: string;
}

export interface DropdownMenuProps {
  /**
   * The control that opens the menu. A single element is cloned so the ARIA
   * wiring and the click handler land on it; anything else is wrapped in a
   * default button.
   */
  trigger: ReactNode;
  items: DropdownMenuItem[];
  /** Which trigger edge the menu aligns to. Defaults to `start`. */
  align?: "start" | "end";
  className?: string;
  menuLabel?: string;
}

interface Position {
  top: number;
  left: number;
  placement: "below" | "above";
}

/** Vertical gap between the trigger and the menu, in px. */
const OFFSET = 4;

export function DropdownMenu({
  trigger,
  items,
  align = "start",
  className,
  menuLabel,
}: DropdownMenuProps) {
  const triggerRef = useRef<HTMLElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const firstEnabled = items.findIndex((item) => item.disabled !== true);
  const lastEnabled = items.reduce(
    (found, item, index) => (item.disabled === true ? found : index),
    -1,
  );

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    setActiveIndex(-1);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  /** Below-start by default; flips above when the menu would clip the fold. */
  const reposition = useCallback(() => {
    const anchor = triggerRef.current;
    const menu = menuRef.current;
    if (anchor === null || menu === null) return;

    const rect = anchor.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement: Position["placement"] =
      spaceBelow < menuRect.height + OFFSET && rect.top > spaceBelow
        ? "above"
        : "below";

    const top =
      placement === "below"
        ? rect.bottom + OFFSET
        : rect.top - menuRect.height - OFFSET;
    const rawLeft = align === "start" ? rect.left : rect.right - menuRect.width;
    const left = Math.max(
      OFFSET,
      Math.min(rawLeft, window.innerWidth - menuRect.width - OFFSET),
    );

    setPosition({ top, left, placement });
  }, [align]);

  // Measured before paint, so the menu never flashes at the wrong coordinates.
  useLayoutEffect(() => {
    if (!open) return undefined;

    reposition();
    const onViewportChange = () => reposition();
    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange);
    return () => {
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange);
    };
  }, [open, reposition]);

  // Outside click closes without stealing focus back.
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (menuRef.current?.contains(target) === true) return;
      if (triggerRef.current?.contains(target) === true) return;
      close(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, close]);

  // Move DOM focus with the roving index so the menu reads correctly.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    itemRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  const move = (from: number, delta: number) => {
    const count = items.length;
    if (count === 0) return;
    for (let offset = 1; offset <= count; offset += 1) {
      const next = (from + delta * offset + count * offset) % count;
      if (items[next]?.disabled !== true) {
        setActiveIndex(next);
        return;
      }
    }
  };

  const select = (index: number) => {
    const item = items[index];
    if (item === undefined || item.disabled === true) return;
    close();
    item.onSelect();
  };

  const openMenu = (focus: "first" | "last") => {
    setOpen(true);
    setActiveIndex(focus === "first" ? firstEnabled : lastEnabled);
  };

  const handleTriggerKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (
      event.key === "ArrowDown" ||
      event.key === "Enter" ||
      event.key === " "
    ) {
      event.preventDefault();
      openMenu("first");
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      openMenu("last");
    }
  };

  const handleMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        move(activeIndex, 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        move(activeIndex, -1);
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(firstEnabled);
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(lastEnabled);
        break;
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        close();
        break;
      case "Tab":
        close(false);
        break;
      default:
        break;
    }
  };

  const triggerProps = {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
    },
    "aria-haspopup": "menu" as const,
    "aria-expanded": open,
    onClick: () => (open ? close() : openMenu("first")),
    onKeyDown: handleTriggerKeyDown,
  };

  const triggerNode = isValidElement(trigger) ? (
    cloneElement(trigger as ReactElement<Record<string, unknown>>, triggerProps)
  ) : (
    <button type="button" className="fc-menu__trigger" {...triggerProps}>
      {trigger}
    </button>
  );

  const menuStyle: CSSProperties =
    position === null
      ? { top: 0, left: 0, visibility: "hidden" }
      : { top: position.top, left: position.left };

  const menuClasses = [
    "fc-menu",
    position?.placement === "above" ? "fc-menu--above" : "fc-menu--below",
    open ? "is-open" : null,
    className,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  return (
    <>
      {triggerNode}
      {open && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={menuRef}
              className={menuClasses}
              style={menuStyle}
              role="menu"
              aria-label={menuLabel}
              aria-orientation="vertical"
              onKeyDown={handleMenuKeyDown}
            >
              {items.map((item, index) => (
                <button
                  key={`${String(index)}-${item.label}`}
                  ref={(node) => {
                    itemRefs.current[index] = node;
                  }}
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  tabIndex={index === activeIndex ? 0 : -1}
                  className={
                    item.danger === true
                      ? "fc-menu__item fc-menu__item--danger"
                      : "fc-menu__item"
                  }
                  onClick={() => select(index)}
                  onMouseEnter={() => {
                    if (item.disabled !== true) setActiveIndex(index);
                  }}
                >
                  <span className="fc-menu__icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <span className="fc-menu__label">{item.label}</span>
                  {item.shortcut !== undefined ? (
                    <span className="fc-menu__shortcut">{item.shortcut}</span>
                  ) : null}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
